import { createColumnHelper } from '@tanstack/table-core';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import {
  Fragment, ReactElement, useMemo, useState
} from 'react';
import {
  FiArrowLeft, FiCode, FiCodepen, FiEdit2, FiEye, FiMessageSquare
} from 'react-icons/fi';
import { MdAutorenew } from 'react-icons/md';
import { RiIncreaseDecreaseLine } from 'react-icons/ri';
import { Link, useSearchParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AgentActionMenu } from '@/app/agents/agentActionMenu';
import { AgentScaling } from '@/app/agents/agentScaling/agentScaling';
import { AgentDeploymentStatusPanel } from '@/app/agents/details/agentDeploymentStatusPanel';
import { Button } from '@/components/button/button';
import { DeploymentStatusBadge, getDeploymentStatusTooltip } from '@/components/deploymentStatus/deploymentStatus';
import { Icon } from '@/components/icon/icon';
import { Loader } from '@/components/loader/loader';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { useNativeTable } from '@/components/nativeTable/useNativeTable';
import { PropertyItem } from '@/components/propertyItem/propertyItem';
import { Section, Subsection } from '@/components/section/section';
import { buildColumnClassString } from '@/components/table/table';
import { Tooltip } from '@/components/tooltip/tooltip';
import { Heading } from '@/components/typography/heading';
import { useAgentScale } from '@/hooks/mutations/useAgentMutations';
import { useAgentConfigQuery, useAgentDetailQuery } from '@/hooks/queries/useAgents';
import { useByoaAgentQuery, useByoaAgentsQuery } from '@/hooks/queries/useByoaAgents';
import { useActionLogsQuery, useModelLogsQuery, useRequestLogsQuery } from '@/hooks/queries/useLogs';
import { useMetricsSummaryQuery } from '@/hooks/queries/useObservability';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import {
  AgentAidp,
  AgentLowcode,
  AgentTypeEnum,
  BaseAgent,
  DeploymentStatus
} from '@/types/agent';
import { TimeRange } from '@/types/observability';
import {
  getLatestVersionEntry, getLowCodeAgentDisplayId, getScalingInitialConfig
} from '@/utils/agentUtils';
import { parseParams } from '@/utils/searchParams';
import { formatISODateTime } from '@/utils/timeFormatter';

import { aggregateDailyCountsByGranularity, aggregateTopModelsByGranularity, pickGranularity } from './analyticsUtils';
import { InvocationIdCell } from './invocationIdCell';
import { InvocationsTrendChart } from './invocationsTrendChart';
import { ModelUsageSection } from './modelUsageSection';
import styles from './page.module.scss';
import { TopModelsSection } from './topModelsSection';
import { AgentTraceViewer } from './traceViewer/traceViewer';

type ModelLog = {
  id: string;
  start_time: string;
  end_time: string;
  completion_tokens: number;
  model: string;
};

type ActionLog = {
  id: string;
  start_time: string;
  end_time: string;
};

type RequestLogSummary = {
  id: string;
  start_time: string;
  correlation_id?: string;
};

const modelLogColumnHelper = createColumnHelper<ModelLog>();
const actionLogColumnHelper = createColumnHelper<ActionLog>();
const requestLogColumnHelper = createColumnHelper<RequestLogSummary>();

const TIME_RANGE_OPTIONS: { value: TimeRange; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'all', label: 'All time' }
];


interface ModelLogsTableProps {
  logs: ModelLog[];
  useLocalZone: boolean;
}

function ModelLogsTable( { logs, useLocalZone }: ModelLogsTableProps ) {
  const columns = useMemo( () => [
    modelLogColumnHelper.accessor( 'id', {
      header: 'Invocation ID',
      size: 200,
      cell: info => <InvocationIdCell id={ info.getValue() } />,
      meta: {
        classStr: buildColumnClassString( { width: 2 } )
      }
    } ),
    modelLogColumnHelper.accessor( 'start_time', {
      header: 'Start Time',
      size: 200,
      cell: info => formatISODateTime( info.getValue(), useLocalZone, 'M/d/yyyy, hh:mm a' ),
      meta: {
        classStr: buildColumnClassString( { width: 2 } )
      }
    } ),
    modelLogColumnHelper.display( {
      id: 'duration',
      header: 'Duration',
      size: 100,
      cell: info => {
        const log = info.row.original;
        const durationSeconds = DateTime.fromISO( log.end_time ).diff( DateTime.fromISO( log.start_time ), 'seconds' ).seconds.toFixed( 2 );
        return `${ durationSeconds }s`;
      },
      meta: {
        classStr: buildColumnClassString( { width: 1 } )
      }
    } ),
    modelLogColumnHelper.accessor( 'completion_tokens', {
      header: 'Tokens Used',
      size: 100,
      cell: info => info.getValue(),
      meta: {
        classStr: buildColumnClassString( { width: 1 } )
      }
    } ),
    modelLogColumnHelper.accessor( 'model', {
      header: 'Model',
      size: 200,
      cell: info => info.getValue(),
      meta: {
        classStr: buildColumnClassString( { width: 2 } )
      }
    } )
  ], [ useLocalZone ] );

  const table = useNativeTable( {
    data: logs,
    columns,
    enableSorting: false
  } );

  return <NativeTable table={ table } pagination={ false } tbodyTestId="table-body-model-logs-table" />;
}

interface ActionLogsTableProps {
  logs: ActionLog[];
  useLocalZone: boolean;
}

function ActionLogsTable( { logs, useLocalZone }: ActionLogsTableProps ) {
  const columns = useMemo( () => [
    actionLogColumnHelper.accessor( 'id', {
      header: 'Invocation ID',
      size: 300,
      cell: info => <InvocationIdCell id={ info.getValue() } />,
      meta: {
        classStr: buildColumnClassString( { width: 3 } )
      }
    } ),
    actionLogColumnHelper.accessor( 'start_time', {
      header: 'Start Time',
      size: 200,
      cell: info => formatISODateTime( info.getValue(), useLocalZone, 'M/d/yyyy, hh:mm a' ),
      meta: {
        classStr: buildColumnClassString( { width: 2 } )
      }
    } ),
    actionLogColumnHelper.display( {
      id: 'duration',
      header: 'Duration',
      size: 100,
      cell: info => {
        const log = info.row.original;
        const durationSeconds = DateTime.fromISO( log.end_time ).diff( DateTime.fromISO( log.start_time ), 'seconds' ).seconds.toFixed( 2 );
        return `${ durationSeconds }s`;
      },
      meta: {
        classStr: buildColumnClassString( { width: 1 } )
      }
    } )
  ], [ useLocalZone ] );

  const table = useNativeTable( {
    data: logs,
    columns,
    enableSorting: false
  } );

  return <NativeTable table={ table } pagination={ false } tbodyTestId="table-body-action-logs-table" />;
}

interface RequestLogsTableProps {
  logs: RequestLogSummary[];
  useLocalZone: boolean;
}

function RequestLogsTable( { logs, useLocalZone }: RequestLogsTableProps ) {
  const columns = useMemo( () => [
    requestLogColumnHelper.accessor( 'id', {
      header: 'Request ID',
      size: 200,
      cell: info => <InvocationIdCell id={ info.getValue() } />,
      meta: {
        classStr: buildColumnClassString( { width: 2 } )
      }
    } ),
    requestLogColumnHelper.accessor( 'start_time', {
      header: 'Start Time',
      size: 200,
      cell: info => formatISODateTime( info.getValue(), useLocalZone, 'M/d/yyyy, hh:mm a' ),
      meta: {
        classStr: buildColumnClassString( { width: 2 } )
      }
    } ),
    requestLogColumnHelper.accessor( 'correlation_id', {
      header: 'Correlation ID',
      size: 200,
      cell: info => info.getValue() || '-',
      meta: {
        classStr: buildColumnClassString( { width: 2 } )
      }
    } )
  ], [ useLocalZone ] );

  const table = useNativeTable( {
    data: logs,
    columns,
    enableSorting: false
  } );

  return <NativeTable table={ table } pagination={ false } tbodyTestId="table-body-request-logs-table" />;
}

type SearchParams = {
  id?: string;
  version?: string;
  type?: string;
};

type AgentTypeInfo = {
  agentData: AgentLowcode | AgentAidp;
  constellation: string;
  currentVersion: string;
  displayName: string;
  icon: ReactElement;
  isAidp: boolean;
  isLowCode: boolean;
  shortName: string;
};

const AGENT_TYPE_LOW_CODE = AgentTypeEnum.LOW_CODE.toString();
const AGENT_TYPE_AIDP = AgentTypeEnum.AIDP.toString();

const getEditLinkQuery = ( agentId: string, version: string ): string => {
  const params = new URLSearchParams( { id: agentId, version } );
  return params.toString();
};

const getPlaygroundLinkQuery = ( isLowCode: boolean, agentData: AgentLowcode | AgentAidp, agentType: string, currentVersion: string, agentName: string ): string => {
  if ( isLowCode ) {
    const agentId = ( agentData as AgentLowcode ).agentId;
    const params = new URLSearchParams( { i: agentId, t: agentType, v: currentVersion } );
    return params.toString();
  }
  const params = new URLSearchParams( { an: agentName, t: agentType } );
  return params.toString();
};

function AgentDetailsPage() {
  const [ searchParams ] = useSearchParams();
  const { id: agentId, version, type } = parseParams<SearchParams>( searchParams );
  const [ useLocalZone ] = useLocalZonePreference();
  const [ timeRange, setTimeRange ] = useState<TimeRange>( '7d' );
  const [ detailsTab, setDetailsTab ] = useState<'overview' | 'traces'>( 'overview' );

  const {
    data: agent,
    isLoading: isAgentLoading,
    error: agentError
  } = useAgentDetailQuery( agentId );

  const isAidp = type === 'aidp';
  const { data: byoaAgents } = useByoaAgentsQuery( isAidp );
  // aidp-core keys BYOA agents by constellation name (what the CLI uploads).
  // Fall back to agent name for old-SDK agents where constellation == agent.
  const byoaMatchName = agent?.constellationName ?? agent?.name;
  const byoaAgentFromList = isAidp ? byoaAgents?.find( a => a.name === byoaMatchName ) : undefined;
  const byoaAgentId = byoaAgentFromList?.id ?? undefined;
  const { data: byoaAgent } = useByoaAgentQuery( byoaAgentId, 3000, byoaAgentFromList );

  const [ showScalingModal, setShowScalingModal ] = useState( false );
  const scaleMutation = useAgentScale();
  const scalingInitialConfig = getScalingInitialConfig( byoaAgent );

  const isLowCodeAgent = agent?.type === AGENT_TYPE_LOW_CODE;
  const shouldFetchConfig = isLowCodeAgent && !!version;

  const {
    data: agentConfig,
    isLoading: isConfigLoading,
    error: configError
  } = useAgentConfigQuery(
    agentId,
    version ? parseInt( version ) : undefined,
    shouldFetchConfig
  );

  const isLoading = isAgentLoading || ( shouldFetchConfig && isConfigLoading );
  const error = agentError?.message || configError?.message || '';

  // Fetch logs using React Query
  const {
    data: actionLogsData,
    isLoading: isActionLogsLoading,
    isError: isActionLogsError
  } = useActionLogsQuery( agent?.id, 5, 0 );

  const {
    data: modelLogsData,
    isLoading: isModelLogsLoading,
    isError: isModelLogsError
  } = useModelLogsQuery( agent?.id, 5, 0 );

  const {
    data: requestLogsData,
    isLoading: isRequestLogsLoading,
    isError: isRequestLogsError
  } = useRequestLogsQuery( agent?.id, 5, 0 );

  // Extract logs data, defaulting to empty arrays on error
  const actionLogs = actionLogsData?.agent_actions || [];
  const modelLogs = modelLogsData?.model_invocations || [];
  const requestLogs = requestLogsData?.completion_requests || [];
  const isLoadingLogs = isActionLogsLoading || isModelLogsLoading || isRequestLogsLoading;

  const {
    data: metricsSummary,
    isLoading: isMetricsLoading,
    isError: isMetricsError,
    dataUpdatedAt: metricsUpdatedAt
  } = useMetricsSummaryQuery( agent?.id, timeRange );
  const granularity = metricsSummary ? pickGranularity( metricsSummary.invocations_trend, timeRange ) : 'daily';
  const periodTrend = metricsSummary ? aggregateDailyCountsByGranularity( metricsSummary.invocations_trend, granularity ) : [];
  const periodTopModels = metricsSummary ? aggregateTopModelsByGranularity( metricsSummary.top_models_by_day, granularity ) : [];

  const agentTypeInfo = useMemo( (): AgentTypeInfo | null => {
    if ( !agent ) return null;

    const isLowCode = agent.type === AGENT_TYPE_LOW_CODE;
    const isAidp = agent.type === AGENT_TYPE_AIDP;

    let currentVersion = '';
    let agentData: AgentLowcode | AgentAidp = {} as AgentLowcode | AgentAidp;

    if ( isLowCode ) {
      const lowCodeAgent = agent as BaseAgent<AgentLowcode>;
      const versionData = getLatestVersionEntry( lowCodeAgent.data.versions );
      currentVersion = versionData.version.toString();
      agentData = lowCodeAgent.data;
    } else if ( isAidp ) {
      const aidpAgent = agent as BaseAgent<AgentAidp>;
      currentVersion = aidpAgent.data.version || byoaAgent?.version?.toString() || '-';
      agentData = aidpAgent.data;
    }

    const constellation = isAidp ? ( agent.constellationName ?? '-' ) : '-';
    const shortName = agent.name ?? agent.id;

    return {
      isLowCode,
      isAidp,
      icon: isLowCode ? <FiCode /> : <FiCodepen />,
      displayName: isLowCode ? 'Low-Code Agent' : 'Full-Code Agent',
      constellation,
      shortName,
      currentVersion,
      agentData
    };
  }, [ agent, byoaAgent ] );


  const renderOperationalAnalytics = (): ReactElement => {
    const timeRangeLabel = TIME_RANGE_OPTIONS.find( o => o.value === timeRange )?.label ?? '';

    let lastUpdatedText: string | null = null;
    if ( metricsUpdatedAt > 0 ) {
      const updatedAt = DateTime.fromMillis( metricsUpdatedAt );
      const diffMins = Math.floor( DateTime.now().diff( updatedAt, 'minutes' ).minutes );
      if ( diffMins < 1 ) {
        lastUpdatedText = 'Last updated just now';
      } else if ( diffMins < 60 ) {
        lastUpdatedText = `Last updated ${ diffMins } minute${ diffMins !== 1 ? 's' : '' } ago`;
      } else {
        const diffHours = Math.floor( DateTime.now().diff( updatedAt, 'hours' ).hours );
        lastUpdatedText = `Last updated ${ diffHours } hour${ diffHours !== 1 ? 's' : '' } ago`;
      }
    }

    const headerContent = (
      <div className={ styles.analyticsHeaderContent }>
        <div className={ styles.timeRangeTabs } role="group" aria-label="Time range">
          {TIME_RANGE_OPTIONS.map( ( opt, i ) => (
            <Fragment key={ opt.value }>
              {i > 0 && <span className={ styles.timeRangeSep } aria-hidden="true">|</span>}
              <button
                type="button"
                className={ clsx( styles.timeRangeTab, timeRange === opt.value && styles.timeRangeTabActive ) }
                onClick={ () => setTimeRange( opt.value ) }
                aria-pressed={ timeRange === opt.value }
              >
                {opt.label}
              </button>
            </Fragment>
          ) )}
        </div>
        {lastUpdatedText && <span className={ styles.lastUpdated }>{ lastUpdatedText }</span>}
      </div>
    );

    return (
      <Section title="Operational Analytics" testId="operational-analytics-section" headerContent={ headerContent }>
        {isMetricsLoading ? (
          <div className={ styles.logsLoading }>Loading analytics...</div>
        ) : isMetricsError ? (
          <p className={ styles.analyticsError }>Error loading analytics data</p>
        ) : metricsSummary && metricsSummary.agent_invocations_count === 0 ? (
          <p className={ styles.noLogs }>Operational analytics will appear once this agent receives traffic.</p>
        ) : metricsSummary ? (
          <>
            <Subsection testId="analytics-metrics">
              <div className={ styles.analyticsTopRow }>
                <div className={ styles.metricsCards }>
                  <PropertyItem
                    variant="large"
                    label="Agent Invocations"
                    labelTooltip="An Agent Invocation is one complete run of an agent from request to final response."
                    value={ metricsSummary.agent_invocations_count.toLocaleString() }
                    helperText={ timeRangeLabel }
                  />
                  <PropertyItem
                    variant="large"
                    label="Avg. model calls / invocation"
                    labelTooltip="Average number of model calls made each time this agent runs."
                    value={ metricsSummary.avg_model_calls_per_invocation.toFixed( 2 ) }
                    helperText={ timeRangeLabel }
                  />
                </div>
                <div className={ styles.trendChartContainer }>
                  <div className={ styles.trendChartTitle }>Invocations Trend</div>
                  { periodTrend.length >= 1
                    ? <InvocationsTrendChart data={ periodTrend } />
                    : <p className={ styles.noLogs }>No invocation trend data for this period.</p>}
                </div>
              </div>
            </Subsection>

            <TopModelsSection
              data={ periodTopModels }
              invocationsTrend={ periodTrend }
              timeRangeLabel={ timeRangeLabel }
            />

            <ModelUsageSection
              models={ metricsSummary.model_usage_summary }
              timeRangeLabel={ timeRangeLabel }
            />
          </>
        ) : (
          <p className={ styles.noLogs }>No analytics data available</p>
        )}
      </Section>
    );
  };

  const renderAgentInfo = (): ReactElement => {
    if ( !agent || !agentTypeInfo ) return <></>;

    const { isLowCode, isAidp, icon, currentVersion, agentData, constellation, shortName } = agentTypeInfo;

    const deploymentStatus: DeploymentStatus | null = isLowCode
      ? DeploymentStatus.RUNNING
      : ( byoaAgent?.status ?? null );
    const autoScalingConfig = byoaAgent?.autoScalingConfig;
    const isAutoScaling = !!autoScalingConfig?.enabled;
    const scalingStatus = isAutoScaling ? byoaAgent?.autoScalingStatus : byoaAgent?.replicaStatus;
    const currentReplicas = scalingStatus?.currentReplicas ?? byoaAgent?.replicas;
    const desiredReplicas = scalingStatus?.desiredReplicas;
    const maxReplicas = autoScalingConfig?.maxReplicas ?? 20;
    const isScalingInProgress = currentReplicas !== undefined
      && desiredReplicas !== undefined
      && currentReplicas !== desiredReplicas;

    const scaleValue = currentReplicas === undefined ? undefined : ( () => {
      const countText = isScalingInProgress
        ? `${ currentReplicas } → ${ desiredReplicas } instances`
        : isAutoScaling
          ? `${ currentReplicas } of ${ maxReplicas } instances`
          : `${ currentReplicas } instance${ currentReplicas !== 1 ? 's' : '' }`;
      return isScalingInProgress
        ? <span className={ styles.scaleValue }><MdAutorenew className={ styles.scaleSpinner } />{ countText }</span>
        : countText;
    } )();

    const scaleHelperText = isScalingInProgress
      ? 'Scaling...'
      : isAutoScaling ? 'Auto-scaling: On' : 'Auto-scaling: Off';

    // Owner means canEdit is true - applies to both Low Code and Custom agents
    const canEdit = agent.editable;

    const editQuery = isLowCode ? getEditLinkQuery( ( agentData as AgentLowcode ).agentId, currentVersion ) : '';
    const playgroundQuery = getPlaygroundLinkQuery( isLowCode, agentData, agent.type, currentVersion, agent.id );

    return (
      <div data-testid="agent-info">
        <div className={ styles.header } data-testid="agent-header">
          <div className={ styles.typeIcon } data-testid="agent-type-icon">
            {icon}
          </div>
          <div className={ styles.titleSection } data-testid="agent-title-section">
            <Heading as="h1" size="lg" data-testid="agent-name">{shortName}</Heading>
            {agent.description && (
              <p className={ styles.description } data-testid="agent-description">{agent.description}</p>
            )}
          </div>
          <div className={ styles.actions } data-testid="agent-actions">
            {isLowCode && (
              <Link
                to={ `/agents/edit/?${ editQuery }` }
                className={ styles.buttonLink }
                data-testid={ canEdit ? 'edit-agent-steps-button' : 'view-agent-steps-button' }
              >
                <Button
                  variant='secondary'
                  icon={ <Icon icon={ canEdit ? FiEdit2 : FiEye } /> }
                >
                  { canEdit ? 'Edit Agent Steps' : 'View Agent Steps' }
                </Button>
              </Link>
            )}
            <Link
              to={ `/agents/playground/?${ playgroundQuery }` }
              className={ styles.buttonLink }
              data-testid="playground-button"
            >
              <Button
                variant='primary'
                icon={ <Icon icon={ FiMessageSquare } /> }
              >
                Playground
              </Button>
            </Link>
            {canEdit && (
              <AgentActionMenu
                agentId={ isLowCode ? ( agentData as AgentLowcode ).agentId : ( agentData as AgentAidp ).agentId ?? byoaAgentId ?? agent.id }
                agentName={ isLowCode ? getLowCodeAgentDisplayId( shortName ) : agent.id }
                isAidp={ isAidp }
                isPublic={ agent.isPublic }
                isOff={ deploymentStatus === DeploymentStatus.OFF }
                byoaAgent={ byoaAgent ?? undefined }
              />
            )}
          </div>
        </div>

        <div className={ styles.detailsTabs } data-testid="details-tabs">
          <button
            type="button"
            className={ clsx( styles.detailsTab, detailsTab === 'overview' && styles.detailsTabActive ) }
            onClick={ () => setDetailsTab( 'overview' ) }
          >
            Overview
          </button>
          <button
            type="button"
            className={ clsx( styles.detailsTab, detailsTab === 'traces' && styles.detailsTabActive ) }
            onClick={ () => setDetailsTab( 'traces' ) }
          >
            Traces
          </button>
        </div>

        {detailsTab === 'traces' ? (
          <div className={ styles.details } data-testid="agent-traces">
            <AgentTraceViewer agentId={ agent.id } useLocalZone={ useLocalZone } />
          </div>
        ) : (
          <div className={ styles.details } data-testid="agent-details">

            <Section title="Properties" testId="properties-section">
              <Subsection>
                <div className={ styles.properties } data-testid="properties-list">
                  <div className={ styles.propertiesStatusRow }>
                    {deploymentStatus && (
                      <PropertyItem
                        variant="large"
                        label="Deployment Status"
                        value={ <DeploymentStatusBadge status={ deploymentStatus } variant="large" /> }
                        helperText={ getDeploymentStatusTooltip( deploymentStatus ) }
                      />
                    )}
                    {scaleValue !== undefined && (
                      <PropertyItem
                        variant="large"
                        label="Scale Status"
                        value={ scaleValue }
                        helperText={ scaleHelperText }
                        action={ isAidp && (
                          <Tooltip content="Set desired agent capacity" position="top">
                            <button
                              type="button"
                              className={ styles.scaleIconButton }
                              onClick={ () => setShowScalingModal( true ) }
                              aria-label="Set desired agent capacity"
                            >
                              <Icon icon={ RiIncreaseDecreaseLine } size="sm" />
                            </button>
                          </Tooltip>
                        ) }
                      />
                    )}
                  </div>
                  <PropertyItem label="Type" value={ isLowCode ? 'Low-Code' : 'Full-Code' } />
                  <PropertyItem label="Version" value={ currentVersion } />
                  <PropertyItem label="Editable" value={ agent.editable ? 'Yes' : 'No' } />
                  <PropertyItem label="Public" value={ agent.isPublic ? 'Yes' : 'No' } />
                  {isLowCode && agentConfig && (
                    <>
                      <PropertyItem label="Created" value={ DateTime.fromISO( agentConfig.createdAt ).toLocaleString( DateTime.DATE_SHORT ) } />
                      {agentConfig.url && (
                        <PropertyItem label="URL" value={ agentConfig.url } copyable />
                      )}
                    </>
                  )}
                  {isAidp && (
                    <>
                      <PropertyItem label="Constellation" value={ constellation } />
                      <PropertyItem label="Host" value={ ( agentData as AgentAidp ).host } />
                    </>
                  )}
                  <PropertyItem label="Agent ID" value={ isLowCode ? `low_code.${ agent.name }` : agent.id } copyable labelTooltip="The Agent ID is a unique identifier used to reference an agent when making API calls." />
                </div>
              </Subsection>
            </Section>

            { isAidp && ( deploymentStatus === DeploymentStatus.ERRORED || deploymentStatus === DeploymentStatus.DEGRADED ) && (
              <AgentDeploymentStatusPanel
                deploymentStatus={ deploymentStatus }
                errorMessage={ byoaAgent?.error }
                agentId={ isLowCode ? ( agentData as AgentLowcode ).agentId : ( agentData as AgentAidp ).agentId ?? byoaAgentId ?? agent.id }
              />
            ) }

            {renderOperationalAnalytics()}

            <Section title="Logs" testId="logs-section">
              { !isLoadingLogs && !isModelLogsError && !isActionLogsError && !isRequestLogsError && modelLogs.length === 0 && actionLogs.length === 0 && requestLogs.length === 0 ? (
                <p className={ styles.noLogs }>Logs will appear once this agent receives traffic.</p>
              ) : (
                <>
                  <Subsection testId="model-logs-subsection">
                    <Heading as="h4" size="md" data-testid="model-logs-title">Model Invocations</Heading>
                    {isLoadingLogs ? (
                      <div className={ styles.logsLoading }>Loading logs...</div>
                    ) : isModelLogsError ? (
                      <p className={ styles.errorLogs }>Error loading Model Invocation logs</p>
                    ) : modelLogs.length > 0 ? (
                      <>
                        <ModelLogsTable logs={ modelLogs } useLocalZone={ useLocalZone } />
                        <div className={ styles.viewAllLogs }>
                          <Button
                            variant='secondary'
                            asLink
                            href={ `/logs?${ new URLSearchParams( { agentId: agent.id, logType: 'modelLog' } ).toString() }` }
                          >
                            View all Model Invocation Logs
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p className={ styles.noLogs }>No Model Invocation logs available</p>
                    )}
                  </Subsection>

                  <Subsection testId="request-logs-subsection">
                    <Heading as="h4" size="md" data-testid="request-logs-title">Request / Response</Heading>
                    {isLoadingLogs ? (
                      <div className={ styles.logsLoading }>Loading logs...</div>
                    ) : isRequestLogsError ? (
                      <p className={ styles.errorLogs }>Error loading Request / Response logs</p>
                    ) : requestLogs.length > 0 ? (
                      <>
                        <RequestLogsTable logs={ requestLogs } useLocalZone={ useLocalZone } />
                        <div className={ styles.viewAllLogs }>
                          <Button
                            variant='secondary'
                            asLink
                            href={ `/logs?${ new URLSearchParams( { agentId: agent.id, logType: 'requestResponseLog' } ).toString() }` }
                          >
                            View all Request / Response Logs
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p className={ styles.noLogs }>No Request / Response logs available</p>
                    )}
                  </Subsection>

                  <Subsection testId="action-logs-subsection">
                    <Heading as="h4" size="md" data-testid="action-logs-title">Agent Actions</Heading>
                    {isLoadingLogs ? (
                      <div className={ styles.logsLoading }>Loading logs...</div>
                    ) : isActionLogsError ? (
                      <p className={ styles.errorLogs }>Error loading Agent Action logs</p>
                    ) : actionLogs.length > 0 ? (
                      <>
                        <ActionLogsTable logs={ actionLogs } useLocalZone={ useLocalZone } />
                        <div className={ styles.viewAllLogs }>
                          <Button
                            variant='secondary'
                            asLink
                            href={ `/logs?${ new URLSearchParams( { agentId: agent.id, logType: 'actionLog' } ).toString() }` }
                          >
                            View all Agent Action Logs
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p className={ styles.noLogs }>No Agent Actions logs available</p>
                    )}
                  </Subsection>
                </>
              ) }
            </Section>
          </div>
        )}
      </div>
    );
  };

  const render = (): ReactElement => {
    if ( error ) {
      return (
        <div className={ styles.error } data-testid="error-container">
          <Heading as="h2" size="md" data-testid="error-title">Error</Heading>
          <p data-testid="error-message">{error}</p>
          <Button
            variant='secondary'
            icon={ <Icon icon={ FiArrowLeft } /> }
            asLink
            href="/agents"
            data-testid="error-back-button"
          >
            Back to Agents
          </Button>
        </div>
      );
    }

    return (
      <>
        <div className={ styles.navigation } data-testid="navigation">
          <Button
            variant='secondary'
            icon={ <Icon icon={ FiArrowLeft } /> }
            asLink
            href="/agents"
            data-testid="back-button"
          >
            Back to Agents
          </Button>
        </div>
        {renderAgentInfo()}
      </>
    );
  };

  return (
    <div className="page">
      {isLoading ? <Loader /> : render()}
      <ToastContainer />
      { showScalingModal && agent && (
        <AgentScaling
          initialConfig={ scalingInitialConfig }
          onApply={ ( config ) => scaleMutation.mutate(
            { agentId: agent.id, ...config },
            { onSettled: () => setShowScalingModal( false ) }
          ) }
          onCancel={ () => setShowScalingModal( false ) }
        />
      ) }
    </div>
  );
}

export default function Page() {
  return (
    <AgentDetailsPage />
  );
}